<header class="app-header" [class.scrolled]="scrolled()" [class.menu-open]="companyMenuOpen() || userMenuOpen() || searchOpen() || bellOpen() || switchingCompany()">
  <!-- Left Zone -->
  <div class="header-left">
    <!-- Logo — lands on the Dashboard home. -->
    <!-- Sidebar collapsed: just the SiteWare mark; open: the full logo. -->
    <a [routerLink]="homeRoute" class="header-logo">
      @if (sidebarCollapsed()) {
        <img class="header-logo-icon" src="/images/siteware-icon.png" alt="SiteWare">
      } @else {
        <img class="header-logo-img" src="/images/siteware-logo.png" alt="SiteWare — Empowering Smarter Sites">
      }
    </a>

    <!-- Menu: collapses / expands the sidebar (opens the drawer on small screens). -->
    <button class="icon-btn" (click)="toggleMenu()" aria-label="Toggle menu">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
    </button>
  </div>

  <!-- Center Zone — Search -->
  <div class="header-center">
    <div class="search-bar" [class.focused]="searchFocused()">
      <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
      <input type="text" placeholder="Search contacts and companies…" class="search-input"
        [value]="searchQuery()"
        (focus)="searchFocused.set(true); searchQuery().trim() && searchOpen.set(true)" (blur)="searchFocused.set(false)"
        (input)="onSearchInput($any($event.target).value)">
      <span class="search-shortcut">⌘K</span>
    </div>

    @if (searchOpen()) {
      <div class="header-search-panel">
        @if (searchLoading()) {
          <div class="hsp-state">Searching…</div>
        } @else if (searchResults().length === 0) {
          <div class="hsp-state">No matches for “{{ searchQuery().trim() }}”.</div>
        } @else {
          @for (r of searchResults(); track r.type + r.id) {
            <button class="hsp-item" (mousedown)="goToResult(r)">
              <span class="hsp-avatar" [class.company]="r.type === 'company'">{{ r.label.charAt(0) }}</span>
              <span class="hsp-body">
                <span class="hsp-label">{{ r.label }}</span>
                <span class="hsp-sub">{{ r.sublabel }}</span>
              </span>
              <span class="hsp-type">{{ r.type }}</span>
            </button>
          }
          @if (searchHasMore()) {
            <button class="hsp-seeall" (mousedown)="seeAllContacts()">
              See all contacts matching “{{ searchQuery().trim() }}” →
            </button>
          }
        }
      </div>
    }
  </div>

  <!-- No manual product switch: the selected company's ModuleType (OS/STR) decides the menus. -->

  <!-- Right Zone -->
  <div class="header-right">
    <!-- Company switcher (right-aligned; only once a company is selected) -->
    @if (selectedCompany(); as sel) {
      <div class="company-switcher">
        <button class="csw-btn" (click)="toggleCompanyMenu()" [disabled]="switchingCompany()">
          <span class="csw-dot"></span>
          <span class="csw-name">{{ sel.name }}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        @if (companyMenuOpen()) {
          <div class="csw-menu csw-menu-right">
            <div class="csw-menu-label">Switch company</div>
            @for (c of companies(); track c.id) {
              <button class="csw-item" [class.active]="c.id === sel.id" (click)="switchCompany(c.id)"
                      [disabled]="switchingCompany()">
                <span class="csw-item-name">{{ c.name }}</span>
                @if (c.id === sel.id) { <span class="csw-check">✓</span> }
              </button>
            }
          </div>
        }
      </div>
    }

    <!-- Full-screen loader while switching company (the app reloads under the new scope). -->
    @if (switchingCompany()) {
      <div class="company-switch-overlay">
        <div class="cso-box">
          <span class="cso-spinner"></span>
          <span class="cso-text">Switching company…</span>
        </div>
      </div>
    }

    <!-- Notifications -->
    <div class="notif-wrapper">
      <button class="icon-btn notif-btn" aria-label="Notifications" [attr.aria-expanded]="bellOpen()" (click)="toggleBell()">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        @if (inbox.unreadCount() > 0) {
          <span class="notif-badge">{{ inbox.unreadCount() > 9 ? '9+' : inbox.unreadCount() }}</span>
        }
      </button>
      @if (bellOpen()) {
        <div class="notif-panel">
          <div class="notif-head">
            <span>Notifications</span>
            @if (inbox.unreadCount() > 0) {
              <button type="button" class="notif-mark-all" (click)="inbox.markAllRead()">Mark all as read</button>
            }
          </div>
          <div class="notif-list">
            @for (n of inbox.items(); track n.id) {
              <button type="button" class="notif-item" [class.unread]="!n.is_read" (click)="openNotification(n)">
                <span class="notif-dot"></span>
                <span class="notif-text">
                  <span class="notif-title">{{ n.title }}</span>
                  @if (n.body) { <span class="notif-body">{{ n.body }}</span> }
                  <span class="notif-time">{{ timeAgo(n.created_date_time) }}</span>
                </span>
              </button>
            } @empty {
              <div class="notif-empty">You're all caught up.</div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Help -->
    <button class="icon-btn hide-mobile" aria-label="Help">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
    </button>

    <!-- User Menu -->
    <div class="user-menu-wrapper" (click)="$event.stopPropagation()">
      <button class="user-btn" (click)="toggleUserMenu()">
        <div class="sw-avatar md">
          @if (avatarUrl()) { <img [src]="avatarUrl()" alt="" class="sw-avatar-img"/> }
          @else { {{ getInitials(currentUser()?.full_name || 'User') }} }
        </div>
        <span class="user-name hide-mobile">{{ currentUser()?.full_name || 'User' }}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="hide-mobile"><path d="M6 9l6 6 6-6"/></svg>
      </button>

      @if (userMenuOpen()) {
        <div class="sw-dropdown user-dropdown">
          <div class="dropdown-user-info">
            <div class="sw-avatar lg">
              @if (avatarUrl()) { <img [src]="avatarUrl()" alt="" class="sw-avatar-img"/> }
              @else { {{ getInitials(currentUser()?.full_name || 'User') }} }
            </div>
            <div>
              <div class="user-full-name">{{ currentUser()?.full_name }}</div>
              <div class="user-email">{{ currentUser()?.email }}</div>
            </div>
          </div>
          <div class="dropdown-sep"></div>
          <button class="dropdown-item" routerLink="/settings" (click)="userMenuOpen.set(false)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            My Profile
          </button>
          <button class="dropdown-item" routerLink="/settings" (click)="userMenuOpen.set(false)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
            Settings
          </button>
          <div class="dropdown-sep"></div>
          <button class="dropdown-item danger" (click)="logout(); userMenuOpen.set(false)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </button>
        </div>
      }
    </div>
  </div>
</header>
